import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Clock, Play } from 'lucide-react';
import type { TaskScheduleToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { formatDateTime } from '~/lib/format-date';
import { useTaskSchedulesList, useTaskLogVerbosities, useTaskScheduleMutations } from '~/hooks/use-task-schedules';
import { tokenToForm } from '~/lib/schemas/task-schedule';

/** Task schedules surface — list with enable toggle, an immediate-run action, and edit/delete. */
export function TaskSchedulesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useTaskSchedulesList();
	const { data: verbosities = [] } = useTaskLogVerbosities();
	const { remove, run } = useTaskScheduleMutations();

	const [deleteRow, setDeleteRow] = useState<TaskScheduleToken | null>(null);

	const verbMap = useMemo(() => new Map(verbosities.map(v => [v.id, v.nm])), [verbosities]);

	const columns = useMemo<SystemGridColumn<TaskScheduleToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.taskSchedules.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.nm,
				exportValue: s => s.nm,
				render: s => <span className="truncate text-sm font-medium text-foreground">{s.nm}</span>
			},
			{
				key: 'enabled',
				header: t('system.taskSchedules.columns.enabled'),
				width: '90px',
				align: 'center',
				sortValue: s => (s.en ? 1 : 0),
				exportValue: s => (s.en ? t('common.yes') : t('common.no')),
				render: s => (s.en ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'interval',
				header: t('system.taskSchedules.columns.interval'),
				width: '100px',
				align: 'right',
				sortValue: s => s.ri ?? 0,
				exportValue: s => s.ri ?? 0,
				render: s => <span className="text-sm tabular-nums text-foreground-muted">{s.ri}</span>
			},
			{
				key: 'lastRun',
				header: t('system.taskSchedules.columns.lastRun'),
				width: '170px',
				sortValue: s => (s.lrsd_l ? String(s.lrsd_l) : ''),
				exportValue: s => formatDateTime(s.lrsd_l),
				render: s => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(s.lrsd_l)}</span>
			},
			{
				key: 'lastOutcome',
				header: t('system.taskSchedules.columns.lastOutcome'),
				width: '110px',
				align: 'center',
				sortValue: s => (s.lrs == null ? 0 : s.lrs ? 1 : 2),
				exportValue: s => (s.lrs == null ? '' : s.lrs ? t('system.taskSchedules.outcomeOk') : t('system.taskSchedules.outcomeFailed')),
				// Written by the TaskScheduler when a run finishes; the error message is in the tooltip.
				render: s =>
					s.lrs == null ? (
						<span className="text-foreground-disabled">—</span>
					) : (
						<Badge variant={s.lrs ? 'default' : 'destructive'} title={s.lem ?? undefined}>
							{s.lrs ? t('system.taskSchedules.outcomeOk') : t('system.taskSchedules.outcomeFailed')}
						</Badge>
					)
			},
			{
				key: 'heartbeat',
				header: t('system.taskSchedules.columns.heartbeat'),
				width: '170px',
				defaultVisible: false,
				sortValue: s => (s.lhd_l ? String(s.lhd_l) : ''),
				exportValue: s => formatDateTime(s.lhd_l),
				render: s => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(s.lhd_l)}</span>
			},
			{
				key: 'nextRun',
				header: t('system.taskSchedules.columns.nextRun'),
				width: '170px',
				sortValue: s => (s.lsd_l ? String(s.lsd_l) : ''),
				exportValue: s => formatDateTime(s.lsd_l),
				render: s => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(s.lsd_l)}</span>
			},
			{
				key: 'lastRunEnd',
				header: t('system.fields.lastRunEnd'),
				width: '170px',
				defaultVisible: false,
				sortValue: s => (s.lred_l ? String(s.lred_l) : ''),
				exportValue: s => formatDateTime(s.lred_l),
				render: s => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(s.lred_l)}</span>
			},
			{
				key: 'firstRun',
				header: t('system.fields.firstRun'),
				width: '170px',
				defaultVisible: false,
				sortValue: s => (s.fstm ? String(s.fstm) : ''),
				exportValue: s => formatDateTime(s.fstm),
				render: s => <span className="text-xs tabular-nums text-foreground-muted">{formatDateTime(s.fstm)}</span>
			},
			{
				key: 'verbosity',
				header: t('system.taskSchedules.columns.verbosity'),
				width: '120px',
				defaultVisible: false,
				sortValue: s => verbMap.get(s.lv ?? -1) ?? '',
				exportValue: s => verbMap.get(s.lv ?? -1) ?? '',
				render: s => <span className="text-sm text-foreground-muted">{verbMap.get(s.lv ?? -1) ?? '—'}</span>
			},
			{
				key: 'errorEmail',
				header: t('system.taskSchedules.errorEmail'),
				width: '200px',
				defaultVisible: false,
				sortValue: s => s.emr ?? '',
				exportValue: s => s.emr ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.emr || '—'}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, verbMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	const onRun = (token: TaskScheduleToken) => {
		run.mutate(tokenToForm(token), {
			onSuccess: () => toast.success(t('system.taskSchedules.runQueued')),
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<TaskScheduleToken>
				gridId="task-schedules"
				title={t('system.taskSchedules.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.nm}
				columns={columns}
				searchPredicate={(s, q) => (s.nm ?? '').toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<Clock size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.taskSchedules.add')}
				onAdd={() => navigate('/system/task-schedules/create')}
				onEdit={s => navigate(`/system/task-schedules/${s.id}`)}
				onDelete={setDeleteRow}
				rowActions={s => (
					<Button
						variant="ghost"
						size="icon"
						type="button"
						aria-label={t('system.taskSchedules.run')}
						title={t('system.taskSchedules.run')}
						disabled={run.isPending}
						onClick={() => onRun(s)}>
						<Play size={15} className="text-brand-subtle-fg" />
					</Button>
				)}
				exportFilename="task-schedules"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.taskSchedules.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.taskSchedules.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
